import type { ClassData, LibraryData, MethodData } from '@/core/types/library.type'

import { analyzeTypeUsageGraftability } from './analyzeTypeUsageGraftability'
import { buildHostedTypeFqns } from './buildHostedTypeFqns'
import type { GraftabilityIssue, GraftabilityMemberKind, LibraryGraftabilityReport } from './types'

function withMemberContext(
  issues: GraftabilityIssue[],
  context: {
    memberName: string
    memberKind: GraftabilityMemberKind
    className: string
    classFqn: string
  }
): GraftabilityIssue[] {
  return issues.map((issue) => ({
    ...issue,
    memberName: context.memberName,
    memberKind: context.memberKind,
    className: context.className,
    classFqn: context.classFqn,
  }))
}

export function analyzeMethodGraftability(
  method: MethodData,
  classData: ClassData,
  hostedTypeFqns: ReadonlySet<string>
): GraftabilityIssue[] {
  const classFqn = `${classData.namespace}.${classData.name}`
  const analysisOptions = { hostedTypeFqns }
  const issues: GraftabilityIssue[] = []

  const returnAnalysis = analyzeTypeUsageGraftability(method.returnTypeUsage, analysisOptions)
  issues.push(
    ...withMemberContext(returnAnalysis.issues, {
      memberName: method.name,
      memberKind: 'return',
      className: classData.name,
      classFqn,
    })
  )

  for (const parameter of method.parameters) {
    const parameterAnalysis = analyzeTypeUsageGraftability(parameter.typeUsage, analysisOptions)
    issues.push(
      ...withMemberContext(parameterAnalysis.issues, {
        memberName: parameter.name,
        memberKind: 'parameter',
        className: classData.name,
        classFqn,
      })
    )
  }

  return issues
}

function analyzeClass(classData: ClassData, hostedTypeFqns: ReadonlySet<string>): GraftabilityIssue[] {
  const classFqn = `${classData.namespace}.${classData.name}`
  const analysisOptions = { hostedTypeFqns }
  const issues: GraftabilityIssue[] = []

  for (const method of Object.values(classData.methods ?? {})) {
    issues.push(...analyzeMethodGraftability(method, classData, hostedTypeFqns))
  }

  for (const field of Object.values(classData.fields ?? {})) {
    const fieldAnalysis = analyzeTypeUsageGraftability(field.fieldTypeUsage, analysisOptions)
    issues.push(
      ...withMemberContext(fieldAnalysis.issues, {
        memberName: field.name,
        memberKind: 'field',
        className: classData.name,
        classFqn,
      })
    )
  }

  for (const constructor of Object.values(classData.constructors ?? {})) {
    for (const parameter of constructor.parameters) {
      const parameterAnalysis = analyzeTypeUsageGraftability(parameter.typeUsage, analysisOptions)
      issues.push(
        ...withMemberContext(parameterAnalysis.issues, {
          memberName: parameter.name,
          memberKind: 'constructor-parameter',
          className: classData.name,
          classFqn,
        })
      )
    }
  }

  return issues
}

function dedupeIssues(issues: GraftabilityIssue[]): GraftabilityIssue[] {
  const seen = new Set<string>()
  return issues.filter((issue) => {
    const key = [issue.kind, issue.typeLabel, issue.memberName, issue.memberKind, issue.classFqn, issue.message].join(
      '|'
    )
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

export function analyzeLibraryGraftability(libraryData: LibraryData): LibraryGraftabilityReport {
  const classEntries = Object.values(libraryData.classes ?? {})
  const hostedTypeFqns = buildHostedTypeFqns(libraryData)
  const issues = dedupeIssues(classEntries.flatMap((classData) => analyzeClass(classData, hostedTypeFqns)))

  const suggestedFacadeTypes = classEntries
    .filter((classData) => analyzeClass(classData, hostedTypeFqns).length === 0)
    .map((classData) => `${classData.namespace}.${classData.name}`)

  const moduleFullName = classEntries.find((classData) => classData.moduleFullName)?.moduleFullName

  return {
    graftable: issues.length === 0,
    issues,
    suggestedFacadeTypes,
    moduleFullName,
  }
}

export function filterGraftabilityIssuesForClass(
  report: LibraryGraftabilityReport,
  classData: ClassData
): LibraryGraftabilityReport {
  const classFqn = `${classData.namespace}.${classData.name}`
  const issues = report.issues.filter((issue) => issue.classFqn === classFqn)

  return {
    ...report,
    issues,
    graftable: issues.length === 0,
    suggestedFacadeTypes: report.suggestedFacadeTypes.filter((fqn) => fqn === classFqn),
  }
}
